import { useEffect, useState } from "react";
import { Phone, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "./Logo";
import { BIZ, NAV } from "@/content";
import { scrollToId } from "@/lib/smooth";

export const Header = ({ onQuote }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 50);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled ? "bg-white/90 py-2 shadow-[0_8px_30px_-12px_rgba(15,43,91,0.25)] backdrop-blur-xl" : "bg-white py-3.5"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-5 lg:px-10">
        <a href="/" data-testid="header-logo-link" aria-label="Plumberoo home" className="shrink-0"><Logo className="h-12 sm:h-14 xl:h-16" /></a>

        <nav className="hidden items-center gap-6 xl:flex" aria-label="Main">
          {NAV.map(([id, label]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)}
              className="group relative text-[0.8rem] font-bold uppercase tracking-[0.12em] text-[#0F2B5B] transition-colors hover:text-[#E05A00]">
              {label}
              <span className="absolute -bottom-1.5 left-0 h-[2px] w-full origin-left scale-x-0 bg-[#FF6B00] transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden flex-col items-end md:flex">
            <a href={BIZ.tel} data-testid="header-phone-link" className="flex items-center gap-2 font-display text-xl font-extrabold text-[#0F2B5B]">
              <Phone className="h-5 w-5 text-[#1E6FE0]" /> {BIZ.phone}
            </a>
            <span className="font-cond text-sm font-bold uppercase tracking-[0.18em] text-[#E05A00]" data-testid="header-licence">Licence {BIZ.licence}</span>
          </div>
          <button data-testid="header-quote-button" onClick={() => onQuote()}
            className="shimmer hidden rounded-full bg-[#FF6B00] px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#E05A00] sm:block">
            Request a quote
          </button>
          <a href={BIZ.tel} aria-label="Call us" data-testid="header-mobile-call" className="grid h-11 w-11 place-items-center rounded-full bg-[#F0F5FA] text-[#1E6FE0] md:hidden">
            <Phone className="h-5 w-5" />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button aria-label="Open menu" data-testid="mobile-menu-button" className="grid h-11 w-11 place-items-center rounded-full border border-slate-200 text-[#0F2B5B] xl:hidden">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] max-w-sm bg-[#0B1F42] text-white" data-testid="mobile-menu">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-6"><Logo light /></div>
              <nav className="mt-10 flex flex-col">
                {NAV.map(([id, label], i) => (
                  <button key={id} data-testid={`mobile-nav-${id}`} onClick={() => go(id)}
                    className="flex items-baseline gap-4 border-b border-white/10 py-4 text-left font-display text-2xl font-bold">
                    <span className="text-xs text-[#FFB27A]">0{i + 1}</span>{label}
                  </button>
                ))}
              </nav>
              <a href={BIZ.tel} className="mt-8 flex items-center gap-2 font-display text-2xl font-extrabold text-[#FF8533]">
                <Phone className="h-5 w-5" /> {BIZ.phone}
              </a>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-white/70">Licence {BIZ.licence}</p>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
